<template>
  <div class="task-tree-container">

    <div v-if="loading" class="loading-wrapper">
      <i class="el-icon-loading"></i>
      <p>加载中...</p>
    </div>
    
    <div v-else-if="error" class="error-wrapper">
      <i class="el-icon-warning-outline"></i>
      <p>{{ error }}</p>
      <el-button type="primary" size="small" @click="loadTaskTree">重试</el-button>
    </div>
    
    <div v-else-if="!taskTree" class="empty-wrapper">
      <i class="el-icon-info"></i>
      <p>请输入任务ID</p>
    </div>
    
    <div v-else class="tree-content">
      <el-tree
        :data="treeData"
        node-key="task_id"
        :props="defaultProps"
        :expand-on-click-node="false"
        :highlight-current="true"
        @node-click="handleNodeClick"
      >
        <template slot="default" slot-scope="{ data }">
          <div class="tree-node-content">
            <div class="node-main">
              <span class="task-name">{{ data.task_name }}</span>
              <el-tag size="small" :type="getStatusType(data.status)">
                {{ getStatusText(data.status) }}
              </el-tag>
            </div>
            <div class="node-meta">
              <span class="task-id">{{ data.task_id }}</span>
              <span class="task-type">{{ getTaskTypeText(data.task_type) }}</span>
              <span class="task-time">{{ formatTime(data.create_time) }}</span>
            </div>
          </div>
        </template>
      </el-tree>
      
      <!-- 任务详情面板 -->
      <div v-if="selectedTask" class="task-detail-panel">
        <div class="panel-header">
          <h4>任务详情</h4>
          <el-button type="text" icon="el-icon-close" @click="selectedTask = null"></el-button>
        </div>
        
        <div class="detail-section">
          <h5>基本信息</h5>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">任务ID:</span>
              <span class="info-value">{{ selectedTask.task_id }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">任务名称:</span>
              <span class="info-value">{{ selectedTask.task_name }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">任务类型:</span>
              <span class="info-value">{{ getTaskTypeText(selectedTask.task_type) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">状态:</span>
              <span class="info-value">{{ getStatusText(selectedTask.status) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">优先级:</span>
              <span class="info-value">{{ getPriorityText(selectedTask.priority) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">创建时间:</span>
              <span class="info-value">{{ formatTime(selectedTask.create_time) }}</span>
            </div>
          </div>
        </div>
        
        <div class="detail-section" v-if="selectedTask.task_description">
          <h5>任务描述</h5>
          <p class="task-description">{{ selectedTask.task_description }}</p>
        </div>
        
        <!-- 会话信息 -->
        <div class="detail-section" v-if="sessionInfo && sessionInfo.length > 0">
          <h5>关联会话</h5>
          <el-table :data="sessionInfo" style="width: 100%" size="small">
            <el-table-column prop="session_id" label="会话ID" width="200"></el-table-column>
            <el-table-column prop="session_symbol" label="会话符号"></el-table-column>
            <el-table-column prop="status" label="状态" width="100">
              <template slot-scope="scope">
                {{ getSessionStatusText(scope.row.status) }}
              </template>
            </el-table-column>
            <el-table-column prop="create_time" label="创建时间" width="180">
              <template slot-scope="scope">
                {{ formatTime(scope.row.create_time) }}
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getTaskHierarchy, getRelatedSessions } from '../api/task'

export default {
  name: 'TaskTree',
  props: {
    taskId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      error: '',
      taskTree: null,
      treeData: [],
      selectedTask: null,
      sessionInfo: []
    }
  },
  watch: {
    taskId: {
      handler(newVal) {
        if (newVal) {
          this.loadTaskTree()
        } else {
          this.taskTree = null
          this.treeData = []
          this.selectedTask = null
          this.sessionInfo = []
        }
      },
      immediate: true
    }
  },
  computed: {
    defaultProps() {
      return {
        children: 'children',
        label: 'task_name'
      }
    }
  },
  methods: {
    async loadTaskTree() {
      if (!this.taskId) return
      
      this.loading = true
      this.error = ''
      
      try {
        const response = await getTaskHierarchy(this.taskId)
        if (response) {
          this.taskTree = response
          this.treeData = [response]
        } else {
          this.error = '获取任务树失败'
        }
      } catch (error) {
        console.error('加载任务树失败:', error)
        this.error = '加载任务树失败'
      } finally {
        this.loading = false
      }
    },
    
    async handleNodeClick(data) {
      this.selectedTask = data
      this.loadSessionInfo(data.task_id)
    },
    
    async loadSessionInfo(taskId) {
      try {
        const response = await getRelatedSessions(taskId)
        if (response && response.sessions) {
          this.sessionInfo = response.sessions
        } else {
          this.sessionInfo = []
        }
      } catch (error) {
        console.error('加载会话信息失败:', error)
        this.sessionInfo = []
      }
    },
    

    
    getStatusType(status) {
      const typeMap = {
        1: 'info',
        2: 'warning',
        3: 'success',
        4: 'danger',
        5: 'primary',
        6: 'info'
      }
      return typeMap[status] || 'info'
    },
    
    getStatusText(status) {
      const textMap = {
        1: '待执行',
        2: '等待中',
        3: '成功',
        4: '失败',
        5: '执行中',
        6: '已取消'
      }
      return textMap[status] || '未知'
    },
    
    getTaskTypeText(type) {
      const textMap = {
        'manual': '手动',
        'auto': '自动',
        'approval': '审批'
      }
      return textMap[type] || type
    },
    
    getPriorityText(priority) {
      const textMap = {
        1: '低',
        2: '中',
        3: '高',
        4: '紧急'
      }
      return textMap[priority] || priority
    },
    
    getSessionStatusText(status) {
      const textMap = {
        1: '待处理',
        2: '处理中',
        3: '已完成',
        4: '失败',
        5: '已取消'
      }
      return textMap[status] || '未知'
    },
    
    formatTime(time) {
      if (!time) return '-'
      const date = new Date(time)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.task-tree-container {
  background-color: var(--bg-card);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
  padding: var(--spacing-lg);
}



.loading-wrapper,
.error-wrapper,
.empty-wrapper {
  padding: var(--spacing-2xl);
  text-align: center;
  color: var(--text-secondary);
  
  i {
    font-size: 48px;
    margin-bottom: var(--spacing-md);
    color: var(--border-color-base);
  }
  
  p {
    margin-bottom: var(--spacing-md);
  }
}

.tree-content {
  display: flex;
  gap: var(--spacing-lg);
  min-height: 100px;
  overflow: hidden;
  
  .el-tree {
    flex: 1;
    min-width: 400px;
    max-height: 600px;
    overflow-y: auto;
  }
}

.task-detail-panel {
  width: 300px;
  border-left: 1px solid var(--border-color-lighter);
  padding-left: var(--spacing-lg);
  overflow-y: auto;
  max-height: 600px;
  flex-shrink: 0;
}

.tree-node-content {
  width: 100%;
  padding: 8px 0;
  border-bottom: 1px solid var(--border-color-extra-light);
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  &:last-child {
    border-bottom: none;
  }
  
  .node-main {
    display: flex;
    align-items: center;
    flex: 1;
    margin-right: var(--spacing-lg);
    
    .task-name {
      font-weight: 500;
      color: var(--text-primary);
      flex: 1;
      margin-right: var(--spacing-sm);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      transition: color 0.3s ease;
      
      &:hover {
        color: var(--primary-color);
      }
    }
    
    .el-tag {
      flex-shrink: 0;
    }
  }
  
  .node-meta {
    display: flex;
    gap: var(--spacing-sm);
    font-size: var(--font-size-xs);
    color: var(--text-secondary);
    flex-wrap: nowrap;
    flex-shrink: 0;
    
    .task-id {
      max-width: 120px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    
    .task-type, .task-time {
      flex-shrink: 0;
    }
  }
}

.task-detail-panel {
  width: 350px;
  border-left: 1px solid var(--border-color-lighter);
  padding-left: var(--spacing-lg);
  overflow-y: auto;
  max-height: 600px;
  
  .panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--spacing-md);
    
    h4 {
      margin: 0;
      color: var(--text-primary);
      font-size: var(--font-size-md);
      font-weight: 600;
    }
  }
  
  .detail-section {
    margin-bottom: var(--spacing-lg);
    
    h5 {
      margin: 0 0 var(--spacing-md) 0;
      color: var(--text-regular);
      font-size: var(--font-size-base);
      font-weight: 500;
    }
  }
  
  .info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--spacing-md);
    
    .info-item {
      display: flex;
      flex-direction: column;
      gap: 4px;
      
      .info-label {
        font-size: var(--font-size-xs);
        color: var(--text-secondary);
      }
      
      .info-value {
        font-size: var(--font-size-sm);
        color: var(--text-primary);
        font-weight: 500;
        word-break: break-all;
      }
    }
  }
  
  .task-description {
    font-size: var(--font-size-sm);
    color: var(--text-regular);
    line-height: 1.4;
  }
}

@media screen and (max-width: 1024px) {
  .tree-content {
    flex-direction: column;
    
    .task-detail-panel {
      width: 100%;
      border-left: none;
      border-top: 1px solid var(--border-color-lighter);
      padding-left: 0;
      padding-top: var(--spacing-lg);
      max-height: 400px;
    }
  }
}

@media screen and (max-width: 768px) {
  .tree-content {
    flex-direction: column;
    
    .task-detail-panel {
      width: 100%;
      border-left: none;
      border-top: 1px solid var(--border-color-lighter);
      padding-left: 0;
      padding-top: var(--spacing-lg);
      max-height: 300px;
    }
  }
  
  .tree-node-content {
    .node-meta {
      flex-direction: column;
      align-items: flex-start;
      gap: 2px;
      
      .task-id {
        max-width: 100%;
      }
    }
  }
}
</style>